import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useContext } from 'react';
import { deleteAgentServiceV3, getAllAgentsServiceV3 } from '../../../services/AgentService';
import { KeyHeader } from '../../shared/TableComponent';

import { BarChart2, Edit, Eye, Plus, TrashIcon } from 'lucide-react';
import { useTranslate } from 'sbx-crm-menu-ui';

import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Permission from 'src/components/shared/Permission';
import { AiAgentV3 } from 'src/types/AiAgentV3';
import { AppAgentContext } from '../../../context/ContextProvider';
import { appActions } from '../../../store';
import { ModalType } from '../../../types/Modal';
import {
    AI_AGENT_V2_CREATE,
    AI_AGENT_V2_DELETE,
    AI_AGENT_V2_EDIT,
    Permissions,
    PermissionType,
} from '../../../types/Permission';
import { DateUtil, grantPermission, validatePermissionModule } from '../../../utils';
import NavbarBreadcrumbV2 from '../../shared/NavbarBreadcrumbV2';
import TableComponentV2 from '../../shared/TableComponentV2';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Button } from '../../ui/button';

// const classNameHeader = 'px-6 py-3 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider';

function addQueryParamsToUrl(url: string, params: Record<string, any>): string {
    const urlObj = new URL(url);

    Object.keys(params).forEach((key) => {
        const value = params[key];

        if (Array.isArray(value)) {
            // Concatenar los elementos del array en una sola cadena separada por comas
            urlObj.searchParams.set(key, value.join(','));
        } else if (value !== undefined && value !== null) {
            // Agregar los valores no array
            urlObj.searchParams.set(key, String(value));
        }
    });

    return urlObj.toString();
}

const AgentListV3Component = () => {
    const { t: common } = useTranslate('common');
    const { t: agent } = useTranslate('agent');
    const { data, isLoading, refetch, isRefetching } = useQuery({
        queryKey: ['agents'],
        queryFn: async ({ meta }) => {
            console.log('meta', meta);
            return await getAllAgentsServiceV3();
        },
    });
    const {
        dispatch,
        state: {
            session: {
                ai_agent_config: { agent_dashboard_params = {} },
                permissions_list,
                permissions,
            },
        },
    } = useContext(AppAgentContext);

    const navigate = useNavigate();

    const [allowEdit, allowDelete] = React.useMemo(() => {
        return [grantPermission(AI_AGENT_V2_EDIT, permissions), grantPermission(AI_AGENT_V2_DELETE, permissions)];
    }, [permissions]);

    const validateAgentByPermissionType = ({
        agent,
        permissionType,
    }: {
        agent: AiAgentV3;
        permissionType: PermissionType;
    }) => {
        const keys = validatePermissionModule({
            permissions_list,
            module: Permissions.SBX_STUDIO_AGENT_V2,
            types: [permissionType],
        });

        if (!agent.id) {
            return false;
        }
        return keys.length === 0 || keys.includes(agent.id);
    };

    React.useEffect(() => {
        refetch({});
    }, []);

    const dataSort = React.useMemo(() => {
        if (data?.success) {
            return data.items
                .filter((agent) => {
                    return validateAgentByPermissionType({ agent, permissionType: PermissionType.READ });
                })
                .sort((a, b) => {
                    let utcDateA: string = new Date().toISOString();
                    let utcDateB: string = new Date().toISOString();
                    if (a.createdAt && b.createdAt) {
                        utcDateA = !a.createdAt.includes('Z') ? a.createdAt + 'Z' : a.createdAt;
                        utcDateB = !b.createdAt.includes('Z') ? b.createdAt + 'Z' : b.createdAt;
                    }
                    return new Date(utcDateB).getTime() - new Date(utcDateA).getTime();
                });
        }
        return [];
    }, [data]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-fuchsia-100 via-sky-100 to-violet-200 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
                <NavbarBreadcrumbV2
                    routes={[
                        {
                            label: agent('agent_list'),
                            route: '',
                        },
                    ]}
                />

                <h1 className="title">{agent('agent_list')}</h1>

                {/*<div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-3xl shadow-xl p-6 mb-8">*/}
                <TableComponentV2
                    items={dataSort}
                    loading={isLoading || isRefetching}
                    tableClassName={'w-full'}
                    topActions={
                        <Permission permission={AI_AGENT_V2_CREATE}>
                            <div>
                                <Button
                                    className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-2 px-4 rounded-full transition-all duration-300 transform hover:scale-105"
                                    onClick={() => {
                                        navigate('/agent_setup_v3/create');
                                    }}
                                >
                                    <Plus size={20} className="mr-2" /> {agent('create_new_agent')}
                                </Button>
                            </div>
                        </Permission>
                    }
                    pagination
                    search
                    header={[
                        {
                            type: KeyHeader.KEY,
                            label: common('name'),
                            key: 'name',
                            // className: classNameHeader,
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: agent('avatar'),
                            // className: classNameHeader,
                            toValue: (item) => {
                                return (
                                    <Avatar>
                                        <AvatarImage src={item.avatar} alt={item.name} />
                                        <AvatarFallback>{item.name[0]}</AvatarFallback>
                                    </Avatar>
                                );
                            },
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: common('createdDate'),
                            // className: classNameHeader,
                            toValue: (item) => {
                                if (item.createdAt) {
                                    const utcDate = !item.createdAt.includes('Z')
                                        ? item.createdAt + 'Z'
                                        : item.createdAt;
                                    return new DateUtil(new Date(utcDate), 'yyyy-MM-dd hh:mm:ss a').getDate();
                                }
                                return new DateUtil(new Date(), 'yyyy-MM-dd hh:mm:ss a').getDate();
                            },
                        },
                    ]}
                    // actionsLabelClassName={`px-6 py-3  text-xs font-semibold text-purple-700 uppercase tracking-wider`}
                    actions={[
                        {
                            label: 'Ver Resultados',
                            icon: <BarChart2 className="h-4 w-4" />,
                            className: 'text-purple-600 hover:text-purple-900 mr-2',
                            onClick: (item) => {
                                const url = `${window.location.origin}/dashboard-ui/chat_dashboard?agent=${item.name.replaceAll(' ', '_')}`;
                                window.location.href = addQueryParamsToUrl(url, agent_dashboard_params);
                            },
                        },
                        {
                            label: allowEdit ? common('edit') : common('view'),
                            icon: allowEdit ? <Edit size={18} /> : <Eye size={18} />,
                            className: ' text-purple-600 hover:text-purple-900 mr-2',
                            visible: (item: AiAgentV3) => {
                                return validateAgentByPermissionType({
                                    agent: item,
                                    permissionType: PermissionType.EDIT,
                                });
                            },
                            onClick: (item: AiAgentV3) => {
                                navigate('/agent_setup_v3/' + item.id);
                            },
                        },
                        ...(allowDelete
                            ? [
                                  {
                                      label: common('delete'),
                                      icon: <TrashIcon className="icon-size" />,
                                      className: `text-red-600 hover:text-red-900`,
                                      visible: (item: AiAgentV3) => {
                                          return validateAgentByPermissionType({
                                              agent: item,
                                              permissionType: PermissionType.DELETE,
                                          });
                                      },
                                      onClick: (item: AiAgentV3) => {
                                          dispatch(
                                              appActions.openModal({
                                                  type: ModalType.CONFIRMATION,
                                                  data: {
                                                      title: `${common('delete')} ${agent('agent')}: "${item?.name ?? ''}"?`,
                                                      titleClassName: 'text-2xl text-center font-bold text-purple-800',
                                                      description: '',

                                                      className:
                                                          '!bg-gradient-to-br !from-purple-50 !to-pink-50 !rounded-3xl !shadow-2xl w-full max-w-md overflow-hidden',
                                                      cancelButtonClassName:
                                                          'border-pink-500 btn text-pink-600 hover:bg-pink-50 w-[200px]',
                                                      confirmButtonClassName:
                                                          'bg-gradient-to-r btn from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white w-[200px]',
                                                      async onConfirm() {
                                                          if (item?.id) {
                                                              const response = await deleteAgentServiceV3(item.id);
                                                              if (response?.success) {
                                                                  toast.success(agent('agent_deleted_successfully'));
                                                              } else {
                                                                  toast.error(agent('agent_deleted_error'));
                                                              }

                                                              refetch();
                                                          }
                                                          dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                                                      },
                                                      onClose() {
                                                          dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                                                      },
                                                  },
                                              })
                                          );
                                      },
                                  },
                              ]
                            : []),
                    ]}
                />
                {/*</div>*/}
            </div>
        </div>
    );
};

export default AgentListV3Component;
